import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import {
	Button,
	Col,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Row,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import organizationalStructureService from "services/OrganizationalStructureService";
import { Trash, Plus, Square, X, CheckSquare } from "@phosphor-icons/react";
import { flatMap } from "lodash";
import ataService from "services/AtaService";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import { Checkbox, IconButton, Tooltip } from "@mui/material";
import indicadoresService from "services/IndicadoresService";
import actionPlansService from "services/ActionPlansService";
import powerBiService from "services/PowerBiService";
import userService from "services/UserService";
import Loading from "components/Common/Loading";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import relatoriosPowerBiService from "services/RelatoriosPowerBiService";
import permissionsService from "services/permissionsService";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const DEPARTMENTS = [
	{ id: 1, name: "Atas" },
	{ id: 2, name: "Indicadores" },
	{ id: 3, name: "Planos de Ação" },
	{ id: 4, name: "Inteligência Empresarial BI" },
];

const ViewingPermissions = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	// Configurações para cada tipo de tabela
	const ATAS_COLUMN_WIDTHS = {
		name: { min: 200, max: 200 },                    // Largura mínima da coluna "Ata" (sem máximo - cresce livremente)
		actions: { min: 60, max: 60 },         // Largura mín/máx da coluna "Ações" (em pixels)
		collaborators: { min: 200 }, // Largura mín/máx da coluna "Quem pode ver" (em pixels)
		id: { min: 60, max: 60 },              // Largura mín/máx da coluna "Marcar" (em pixels)
	};

	const INDICADORES_COLUMN_WIDTHS = {
		"organization.name": { min: 120, max: 200 }, // Largura mín/máx da coluna "Departamento" (em pixels)
		intention_goals: { min: 150, max: 200, },               // Largura mínima da coluna "Indicador" (sem máximo - cresce livremente)
		actions: { min: 60, max: 60 },               // Largura mín/máx da coluna "Ações" (em pixels)
		collaborators: { min: 200 },       // Largura mín/máx da coluna "Quem pode ver" (em pixels)
		id: { min: 60, max: 60 },                    // Largura mín/máx da coluna "Marcar" (em pixels)
	};

	const PLANOS_DE_ACAO_COLUMN_WIDTHS = {
		"organization.name": { min: 120, max: 200 }, // Largura mín/máx da coluna "Departamento" (em pixels)
		guideline: { min: 200 },                     // Largura mínima da coluna "Plano de Ação" (sem máximo - cresce livremente)
		actions: { min: 60, max: 60 },               // Largura mín/máx da coluna "Ações" (em pixels)
		collaborators: { min: 200 },       // Largura mín/máx da coluna "Quem pode ver" (em pixels)
		id: { min: 60, max: 60 },                    // Largura mín/máx da coluna "Marcar" (em pixels)
	};

	const INTELIGENCIA_EMPRESARIAL_COLUMN_WIDTHS = {
		name: { min: 250, max: 300 },           // Largura mínima da coluna "Power BI" (sem máximo - cresce livremente)
		users: { min: 200, }, // Largura mín/máx da coluna "Quem pode ver" (em pixels)
		id: { min: 60, max: 60 },     // Largura mín/máx da coluna "Marcar" (em pixels)
	};
	// ================================================================

	const [departments, setDepartments] = useState([]);
	const [whoCanView, setWhoCanView] = useState([]);
	const [atasData, setAtasData] = useState([]);
	const [indicadoresData, setIndicadoresData] = useState([]);
	const [planosDeAcaoData, setPlanosDeAcaoData] = useState([]);
	const [inteligenciaEmpresarialData, setIntelingenciaEmpresarialData] =
		useState([]);
	const [selectedRows, setSelectedRows] = useState({});
	const [loading, setLoading] = useState(false);
	const [loggedUserId, setLoggedUserId] = useState(null);
	const [disabledRows, setDisabledRows] = useState([]);

	// Hooks para zoom e cálculos de largura para cada tipo de tabela
	const atasZoom = useTableZoom(ATAS_COLUMN_WIDTHS);
	const indicadoresZoom = useTableZoom(INDICADORES_COLUMN_WIDTHS);
	const planosDeAcaoZoom = useTableZoom(PLANOS_DE_ACAO_COLUMN_WIDTHS);
	const inteligenciaEmpresarialZoom = useTableZoom(INTELIGENCIA_EMPRESARIAL_COLUMN_WIDTHS);

	document.title = "Senda | Permissões de visualização";

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			department: 1,
			whoCanView: [],
		},

		onSubmit: async (values) => {
			try {
				setLoading(true);
				if (values.department === 1) {
					for (const [id, isSelected] of Object.entries(
						selectedRows,
					)) {
						if (isSelected) {
							const existingCollaboratorsResponse =
								await ataService.getById(id);

							const existingCollaborators =
								existingCollaboratorsResponse.collaborators ||
								[];

							const existingCollaboratorsIds =
								existingCollaborators.map(
									(collaborator) => collaborator.id,
								);

							// new Set([...]): remove duplicatas para garantir que o mesmo ID não apareça duas vezes
							const combinedCollaborators = [
								...new Set([
									...existingCollaboratorsIds,
									...values.whoCanView,
								]),
							];

							const data = {
								collaborators: combinedCollaborators,
							};

							await ataService.put(id, data);
						}
					}
					getAtasData();

					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Colaborador inserido com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
				}
				if (values.department === 2) {
					for (const [id, isSelected] of Object.entries(
						selectedRows,
					)) {
						if (isSelected) {
							const existingCollaboratorsResponse =
								await indicadoresService.getById(id);

							const existingCollaborators =
								existingCollaboratorsResponse.collaborators ||
								[];

							const existingCollaboratorsIds =
								existingCollaborators.map(
									(collaborator) => collaborator.id,
								);

							// new Set([...]): remove duplicatas para garantir que o mesmo ID não apareça duas vezes
							const combinedCollaborators = [
								...new Set([
									...existingCollaboratorsIds,
									...values.whoCanView,
								]),
							];

							const data = {
								collaborators: combinedCollaborators,
							};

							await indicadoresService.put(id, data);
						}
					}
					getIndicadoresData();

					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Colaborador inserido com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
				}
				if (values.department === 3) {
					for (const [id, isSelected] of Object.entries(
						selectedRows,
					)) {
						if (isSelected) {
							const existingCollaboratorsResponse =
								await actionPlansService.getById(id);

							const existingCollaborators =
								existingCollaboratorsResponse.collaborators ||
								[];

							const existingCollaboratorsIds =
								existingCollaborators.map(
									(collaborator) => collaborator.id,
								);

							// new Set([...]): remove duplicatas para garantir que o mesmo ID não apareça duas vezes
							const combinedCollaborators = [
								...new Set([
									...existingCollaboratorsIds,
									...values.whoCanView,
								]),
							];

							const data = {
								collaborators: combinedCollaborators,
							};

							await actionPlansService.put(id, data);
						}
					}
					getPlanosDeAcaoData();

					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Colaborador inserido com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
				}
				if (values.department === 4) {
					for (const [id, isSelected] of Object.entries(
						selectedRows,
					)) {
						if (isSelected) {
							const existingCollaboratorsResponse =
								await relatoriosPowerBiService.getById(id);

							console.log(existingCollaboratorsResponse);

							// Acessando 'users' corretamente
							const existingCollaborators =
								existingCollaboratorsResponse.users || [];

							// Extraindo IDs dos colaboradores existentes
							const existingCollaboratorsIds =
								existingCollaborators.map(
									(collaborator) => collaborator.user.id,
								);

							// Combine os IDs existentes com os novos IDs
							const combinedCollaboratorsIds = [
								...new Set([
									...existingCollaboratorsIds,
									...values.whoCanView,
								]),
							];

							// Criação do objeto no formato esperado pelo backend
							const combinedCollaborators =
								combinedCollaboratorsIds.map((user_id) => {
									const existingCollaborator =
										existingCollaborators.find(
											(collaborator) =>
												collaborator.user.id ===
												user_id,
										);

									if (!existingCollaborator) {
										return {
											user_id,
											rls: null,
											power_bi_id:
												existingCollaboratorsResponse.id,
										};
									}

									return {
										user_id,
										rls: existingCollaborator?.rls || null, // Use `rls` existente ou `null`
										power_bi_id:
											existingCollaborator?.power_bi_id ||
											null, // Adiciona o power_bi_id
									};
								});

							const data = {
								users: combinedCollaborators,
							};

							console.log(data);

							await relatoriosPowerBiService.put(id, data);
						}
					}
					getInteligenciaEmpresarialData();

					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Colaborador inserido com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
				}
			} catch (error) {
				SendaCustomSwal.fire({
					iconHtml: '<i class="error-icon"></i>',
					title: "Oops...",
					text: "Algo deu errado!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			} finally {
				setLoading(false);
			}
		},
	});

	const customStyles = {
		menu: (base) => ({
			...base,
			backgroundColor: "white",
			boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
		}),
	};

	async function getDepartments() {
		try {
			const options = DEPARTMENTS.map((item) => ({
				value: item.id,
				label: item.name,
			}));
			setDepartments(options);
		} catch (error) {
			console.log(error);
		}
	}

	async function getWhoCanView() {
		try {
			const response = await userService.get();
			const options = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			setWhoCanView(options);
		} catch (error) {
			console.log(error);
		}
	}

	async function getAtasData() {
		try {
			const response = await ataService.get();

			const sortedResponse = response.sort((a, b) =>
				a.name.localeCompare(b.name),
			);

			setAtasData(sortedResponse);
			console.log(sortedResponse);
		} catch (error) {
			console.log(error);
		}
	}

	async function getIndicadoresData() {
		try {
			const response = await indicadoresService.get();

			const sortedResponse = response.sort((a, b) =>
				a.organization.name.localeCompare(b.organization.name),
			);

			setIndicadoresData(sortedResponse);
		} catch (error) {
			console.log(error);
		}
	}

	async function getPlanosDeAcaoData() {
		try {
			const response = await actionPlansService.get();

			const sortedResponse = response.sort((a, b) =>
				a.organization.name.localeCompare(b.organization.name),
			);

			setPlanosDeAcaoData(sortedResponse);
		} catch (error) {
			console.log(error);
		}
	}

	async function getInteligenciaEmpresarialData() {
		try {
			const response = await relatoriosPowerBiService.getAll();

			const sortedResponse = response.sort((a, b) =>
				a.name.localeCompare(b.name),
			);

			setIntelingenciaEmpresarialData(sortedResponse);
		} catch (error) {
			console.log(error);
		}
	}

	async function removeCollaborators() {
		try {
			setLoading(true);
			if (validation.values.department === 1) {
				for (const [id, isSelected] of Object.entries(selectedRows)) {
					if (isSelected) {
						const existingCollaboratorsResponse =
							await ataService.getById(id);

						const existingCollaborators =
							existingCollaboratorsResponse.collaborators || [];

						const existingCollaboratorsIds =
							existingCollaborators.map(
								(collaborator) => collaborator.id,
							);

						// Remove os IDs de `validation.values.whoCanView` de `existingCollaboratorsIds`
						const updatedCollaborators =
							existingCollaboratorsIds.filter(
								(collaboratorId) =>
									!validation.values.whoCanView.includes(
										collaboratorId,
									),
							);

						const data = {
							collaborators: updatedCollaborators,
						};

						await ataService.put(id, data);
					}
				}
				getAtasData();

				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: "Colaborador removido com sucesso!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
			if (validation.values.department === 2) {
				for (const [id, isSelected] of Object.entries(selectedRows)) {
					if (isSelected) {
						const existingCollaboratorsResponse =
							await indicadoresService.getById(id);

						const existingCollaborators =
							existingCollaboratorsResponse.collaborators || [];

						const existingCollaboratorsIds =
							existingCollaborators.map(
								(collaborator) => collaborator.id,
							);

						// Remove os IDs de `validation.values.whoCanView` de `existingCollaboratorsIds`
						const updatedCollaborators =
							existingCollaboratorsIds.filter(
								(collaboratorId) =>
									!validation.values.whoCanView.includes(
										collaboratorId,
									),
							);

						const data = {
							collaborators: updatedCollaborators,
						};

						await indicadoresService.put(id, data);
					}
				}
				getIndicadoresData();

				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: "Colaborador removido com sucesso!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
			if (validation.values.department === 3) {
				for (const [id, isSelected] of Object.entries(selectedRows)) {
					if (isSelected) {
						const existingCollaboratorsResponse =
							await actionPlansService.getById(id);

						const existingCollaborators =
							existingCollaboratorsResponse.collaborators || [];

						const existingCollaboratorsIds =
							existingCollaborators.map(
								(collaborator) => collaborator.id,
							);

						// Remove os IDs de `validation.values.whoCanView` de `existingCollaboratorsIds`
						const updatedCollaborators =
							existingCollaboratorsIds.filter(
								(collaboratorId) =>
									!validation.values.whoCanView.includes(
										collaboratorId,
									),
							);

						const data = {
							collaborators: updatedCollaborators,
						};

						await actionPlansService.put(id, data);
					}
				}
				getPlanosDeAcaoData();

				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: "Colaborador removido com sucesso!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
			if (validation.values.department === 4) {
				for (const [id, isSelected] of Object.entries(selectedRows)) {
					if (isSelected) {
						// Obtém a lista de usuários existentes no relatório de Power BI
						const existingCollaboratorsResponse =
							await relatoriosPowerBiService.getById(id);

						// Garante que 'users' seja acessado corretamente
						const existingUsers =
							existingCollaboratorsResponse.users || [];

						console.log(existingUsers);

						// Remove os usuários especificados em `validation.values.whoCanView`
						const updatedUsers = existingUsers.filter(
							(user) =>
								!validation.values.whoCanView.includes(
									user.user_id,
								),
						);

						console.log(updatedUsers);

						// // Cria o payload no formato esperado pelo backend
						const updatedUsersPayload = updatedUsers.map(
							(user) => ({
								user_id: user.user_id,
								rls: user.rls || null, // Mantém o valor de `rls` existente
								power_bi_id: user.power_bi_id || id, // Garante que o `power_bi_id` seja enviado
							}),
						);

						const data = {
							users: updatedUsersPayload,
						};

						console.log(data);

						// // Envia a atualização para o backend
						await relatoriosPowerBiService.put(id, data);
					}
				}
				getInteligenciaEmpresarialData();

				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: "Colaborador removido com sucesso!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
		} catch (error) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: "Algo deu errado!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setLoading(false);
		}
	}

	function isAnyRowSelected() {
		// Verifica se há alguma linha selecionada em `selectedRows`
		const hasSelectedRows = Object.values(selectedRows).some(
			(isSelected) => isSelected,
		);

		// Verifica se ao menos um colaborador foi selecionado em `whoCanView`
		const hasCollaborators =
			validation.values.whoCanView &&
			validation.values.whoCanView.length > 0;

		// Retorna verdadeiro apenas se ambas as condições forem verdadeiras
		return hasSelectedRows && hasCollaborators;
	}

	async function getLoggedUserId() {
		const userPermission = await permissionsService.getUserPermission();
		setLoggedUserId(userPermission.user_id);
	}

	useEffect(() => {
		getDepartments();
		getWhoCanView();
		getAtasData();
		getIndicadoresData();
		getPlanosDeAcaoData();
		getInteligenciaEmpresarialData();
		getLoggedUserId();
	}, []);

	useEffect(() => {
		const getActiveTableData = () => {
			switch (validation.values.department) {
				case 1:
					return atasData;
				case 2:
					return indicadoresData;
				case 3:
					return planosDeAcaoData;
				case 4:
					return inteligenciaEmpresarialData;
				default:
					return [];
			}
		};

		const activeData = getActiveTableData();

		if (activeData.length > 0) {
			const initialState = activeData.reduce((acc, row) => {
				acc[row.id] = false;
				return acc;
			}, {});
			setSelectedRows(initialState);

			// LÓGICA DE DESATIVAÇÃO DE LINHAS COMENTADA - TODAS AS LINHAS FICAM ATIVAS
			// const disabledRowIds = activeData
			// 	.filter((row) => {
			// 		if (validation.values.department === 4) {
			// 			// Caso inteligenciaEmpresarialData use "users"
			// 			return !row.users?.some(
			// 				(userObj) =>
			// 					userObj.user?.id == loggedUserId,
			// 			);
			// 		}
			// 		
			// 		// Verifica se o usuário está na lista de colaboradores
			// 		const isCollaborator = row.collaborators?.some(
			// 			(collaborator) => collaborator.id == loggedUserId,
			// 		);
			// 		
			// 		// Verifica se o usuário é dono/responsável
			// 		let isOwnerOrResponsible = false;
			// 		if (validation.values.department === 1) {
			// 			// Atas: verifica owner_id
			// 			isOwnerOrResponsible = row.owner_id == loggedUserId;
			// 		} else if (validation.values.department === 2) {
			// 			// Indicadores (Goals): verifica responsible_id
			// 			isOwnerOrResponsible = row.responsible_id == loggedUserId;
			// 		} else if (validation.values.department === 3) {
			// 			// Planos de Ação: verifica responsible_id
			// 			isOwnerOrResponsible = row.responsible_id == loggedUserId;
			// 		}
			// 		
			// 		// Linha fica desabilitada se não é colaborador E não é dono/responsável
			// 		return !isCollaborator && !isOwnerOrResponsible;
			// 	})
			// 	.map((row) => row.id); // Mapeia os IDs das linhas desabilitadas

			// setDisabledRows(disabledRowIds);

			// Atualizar o estado das linhas desabilitadas - deixando sempre vazio para todas ficarem ativas
			setDisabledRows([]);
		}
	}, [
		validation.values.department,
		atasData,
		indicadoresData,
		planosDeAcaoData,
		inteligenciaEmpresarialData,
		// loggedUserId, // Comentado pois não é mais necessário para lógica de desativação
	]);

	const isRowDisabled = (rowData, userId) => {
		return !rowData.collaborators.some(
			(collaborator) => collaborator.id == userId,
		);
	};

	// useEffect(() => {
	// 	console.log(selectedRows); // Verifique o estado atualizado
	// }, [selectedRows]);

	const atasColumn = useMemo(
		() => [
			{
				header: "Ata",
				accessorKey: "name",
				widthPixels: atasZoom.getColumnWidth('name'),
				minWidthPixels: ATAS_COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					return (
						<div
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{row.getValue("name")}
						</div>
					);
				},
			},
			{
				header: "Ações",
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: atasZoom.getColumnWidth('actions'),
				minWidthPixels: ATAS_COLUMN_WIDTHS.actions.min,
				maxWidthPixels: ATAS_COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					const handleDelete = async () => {
						const result = await SendaCustomSwal.fire({
							iconHtml: '<i class="warning-icon"></i>',
							title: "Confirmar exclusão",
							text: `Tem certeza que deseja excluir a ata "${row.original.name}"?`,
							showCancelButton: true,
							confirmButtonText: "Sim, excluir",
							cancelButtonText: "Cancelar",
							customClass: {
								confirmButton: "custom-confirm-button",
								cancelButton: "custom-cancel-button",
							},
						});

						if (result.isConfirmed) {
							try {
								setLoading(true);
								console.log("Excluindo ata:", row.original);
								await ataService.delete(row.original.id);
								getAtasData();
								SendaCustomSwal.fire({
									iconHtml: '<i class="success-icon"></i>',
									title: "Sucesso",
									text: "Ata removida com sucesso!",
									customClass: {
										confirmButton: "custom-confirm-button",
									},
								});
							} catch (error) {
								console.error("Erro ao excluir ata:", error);
								SendaCustomSwal.fire({
									iconHtml: '<i class="error-icon"></i>',
									title: "Erro",
									text: "Erro ao excluir a ata. Tente novamente.",
									customClass: {
										confirmButton: "custom-confirm-button",
									},
								});
							} finally {
								setLoading(false);
							}
						}
					};

					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<Tooltip title="Excluir" placement="top">
								<IconButton
									onClick={handleDelete}
									disabled={isDisabled}
									style={{
										padding: "1px",
										cursor: isDisabled ? "not-allowed" : "pointer",
										opacity: isDisabled ? 0.5 : 1,
									}}
								>
									<Trash size={14} color={"black"} />
								</IconButton>
							</Tooltip>
						</div>
					);
				},
			},
			{
				header: "Quem pode ver",
				accessorKey: "collaborators",
				filterFn: "contains",
				widthPixels: atasZoom.getColumnWidth('collaborators'),
				minWidthPixels: ATAS_COLUMN_WIDTHS.collaborators.min,
				maxWidthPixels: ATAS_COLUMN_WIDTHS.collaborators.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				accessorFn: (row) => {
					const collaborators = row.collaborators || [];
					return collaborators.map((c) => c.lastname).join(", ");
				},
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					const collaborators = row.original.collaborators || [];
					const collaboratorNames = collaborators
						.map((c) => c.lastname)
						.join(", ");

					return (
						<span
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{collaboratorNames}
						</span>
					);
				},
			},
			{
				header: <span>Marcar</span>,
				accessorKey: "id",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: atasZoom.getColumnWidth('id'),
				minWidthPixels: ATAS_COLUMN_WIDTHS.id.min,
				maxWidthPixels: ATAS_COLUMN_WIDTHS.id.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<div
								style={{
									marginLeft: 12,
									cursor: isDisabled
										? "not-allowed"
										: "pointer", // Aplicando o cursor aqui
									opacity: isDisabled ? 0.5 : 1, // Diminui a opacidade se estiver desabilitado
								}}
							>
								<Checkbox
									className="form-check-input"
									key={`checkbox-${row.original.id}`}
									id={`checkbox-${row.original.id}`}
									name={`checkbox-${row.original.id}`}
									checked={!!selectedRows[row.original.id]}
									onChange={(event) => {
										if (isDisabled) return; // Não faz nada se a linha estiver desabilitada
										const value = event.target.checked;
										setSelectedRows((prev) => ({
											...prev,
											[row.original.id]: value,
										}));
									}}
									style={{
										background: "none",
										border: "none",
										padding: 0,
										color: "black",
									}}
									icon={<Square size={16} />}
									checkedIcon={<CheckSquare size={16} />}
									disabled={isDisabled} // Desativa o checkbox se a linha for desabilitada
								/>
							</div>
						</div>
					);
				},
			},
		],
		[selectedRows, atasZoom.zoomLevel], // Adicionado zoomLevel como dependência
	);

	const indicadoresColumn = useMemo(
		() => [
			{
				header: "Departamento",
				accessorKey: "organization.name",
				widthPixels: indicadoresZoom.getColumnWidth('organization.name'),
				minWidthPixels: INDICADORES_COLUMN_WIDTHS["organization.name"].min,
				maxWidthPixels: INDICADORES_COLUMN_WIDTHS["organization.name"].max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					return (
						<div
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{row.getValue("organization.name")}
						</div>
					);
				},
			},
			{
				header: "Indicador",
				accessorKey: "intention_goals",
				filterFn: "contains",
				widthPixels: indicadoresZoom.getColumnWidth('intention_goals'),
				minWidthPixels: INDICADORES_COLUMN_WIDTHS.intention_goals.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					return (
						<div
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{row.getValue("intention_goals")}
						</div>
					);
				},
			},
			{
				header: "Ações",
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: indicadoresZoom.getColumnWidth('actions'),
				minWidthPixels: INDICADORES_COLUMN_WIDTHS.actions.min,
				maxWidthPixels: INDICADORES_COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					const handleDelete = async () => {
						const result = await SendaCustomSwal.fire({
							iconHtml: '<i class="warning-icon"></i>',
							title: "Confirmar exclusão",
							text: `Tem certeza que deseja excluir o indicador "${row.original.intention_goals}"?`,
							showCancelButton: true,
							confirmButtonText: "Sim, excluir",
							cancelButtonText: "Cancelar",
							customClass: {
								confirmButton: "custom-confirm-button",
								cancelButton: "custom-cancel-button",
							},
						});

						if (result.isConfirmed) {
							try {
								setLoading(true);
								console.log("Excluindo indicador:", row.original);
								await indicadoresService.delete(row.original.id);
								getIndicadoresData();
								SendaCustomSwal.fire({
									iconHtml: '<i class="success-icon"></i>',
									title: "Sucesso",
									text: "Indicador removido com sucesso!",
									customClass: {
										confirmButton: "custom-confirm-button",
									},
								});
							} catch (error) {
								console.error("Erro ao excluir indicador:", error);
								SendaCustomSwal.fire({
									iconHtml: '<i class="error-icon"></i>',
									title: "Erro",
									text: "Erro ao excluir o indicador. Tente novamente.",
									customClass: {
										confirmButton: "custom-confirm-button",
									},
								});
							} finally {
								setLoading(false);
							}
						}
					};

					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<Tooltip title="Excluir" placement="top">
								<IconButton
									onClick={handleDelete}
									disabled={isDisabled}
									style={{
										padding: "1px",
										cursor: isDisabled ? "not-allowed" : "pointer",
										opacity: isDisabled ? 0.5 : 1,
									}}
								>
									<Trash size={14} color={"black"} />
								</IconButton>
							</Tooltip>
						</div>
					);
				},
			},
			{
				header: "Quem pode ver",
				accessorKey: "collaborators",
				filterFn: "contains",
				widthPixels: indicadoresZoom.getColumnWidth('collaborators'),
				minWidthPixels: INDICADORES_COLUMN_WIDTHS.collaborators.min,
				maxWidthPixels: INDICADORES_COLUMN_WIDTHS.collaborators.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				accessorFn: (row) => {
					const collaborators = row.collaborators || [];
					return collaborators.map((c) => c.lastname).join(", ");
				},
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					const collaborators = row.original.collaborators || [];
					const collaboratorNames = collaborators
						.map((c) => c.lastname)
						.join(", ");

					return (
						<span
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{collaboratorNames}
						</span>
					);
				},
			},
			{
				header: <span>Marcar</span>,
				accessorKey: "id",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: indicadoresZoom.getColumnWidth('id'),
				minWidthPixels: INDICADORES_COLUMN_WIDTHS.id.min,
				maxWidthPixels: INDICADORES_COLUMN_WIDTHS.id.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<div
								style={{
									marginLeft: 12,
									cursor: isDisabled
										? "not-allowed"
										: "pointer", // Aplicando o cursor aqui
									opacity: isDisabled ? 0.5 : 1, // Diminui a opacidade se estiver desabilitado
								}}
							>
								<Checkbox
									className="form-check-input"
									key={`checkbox-${row.original.id}`}
									id={`checkbox-${row.original.id}`}
									name={`checkbox-${row.original.id}`}
									checked={!!selectedRows[row.original.id]}
									onChange={(event) => {
										if (isDisabled) return; // Não faz nada se a linha estiver desabilitada
										const value = event.target.checked;
										setSelectedRows((prev) => ({
											...prev,
											[row.original.id]: value,
										}));
									}}
									style={{
										background: "none",
										border: "none",
										padding: 0,
										color: "black",
									}}
									icon={<Square size={16} />}
									checkedIcon={<CheckSquare size={16} />}
									disabled={isDisabled} // Desativa o checkbox se a linha for desabilitada
								/>
							</div>
						</div>
					);
				},
			},
		],
		[selectedRows, indicadoresZoom.zoomLevel], // Adicionado zoomLevel como dependência
	);

	const planosDeAcaoColumn = useMemo(
		() => [
			{
				header: "Departamento",
				accessorKey: "organization.name",
				widthPixels: planosDeAcaoZoom.getColumnWidth('organization.name'),
				minWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS["organization.name"].min,
				maxWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS["organization.name"].max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				filterFn: "contains",
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					return (
						<div
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{row.getValue("organization.name")}
						</div>
					);
				},
			},
			{
				header: "Plano de Ação",
				accessorKey: "guideline",
				widthPixels: planosDeAcaoZoom.getColumnWidth('guideline'),
				minWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS.guideline.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				filterFn: "contains",
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					return (
						<div
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{row.getValue("guideline")}
						</div>
					);
				},
			},
			{
				header: "Ações",
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: planosDeAcaoZoom.getColumnWidth('actions'),
				minWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS.actions.min,
				maxWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					const handleDelete = async () => {
						const result = await SendaCustomSwal.fire({
							iconHtml: '<i class="warning-icon"></i>',
							title: "Confirmar exclusão",
							text: `Tem certeza que deseja excluir o plano de ação "${row.original.guideline}"?`,
							showCancelButton: true,
							confirmButtonText: "Sim, excluir",
							cancelButtonText: "Cancelar",
							customClass: {
								confirmButton: "custom-confirm-button",
								cancelButton: "custom-cancel-button",
							},
						});

						if (result.isConfirmed) {
							try {
								setLoading(true);
								console.log("Excluindo plano de ação:", row.original);
								await actionPlansService.delete(row.original.id);
								getPlanosDeAcaoData();
								SendaCustomSwal.fire({
									iconHtml: '<i class="success-icon"></i>',
									title: "Sucesso",
									text: "Plano de ação removido com sucesso!",
									customClass: {
										confirmButton: "custom-confirm-button",
									},
								});
							} catch (error) {
								console.error("Erro ao excluir plano de ação:", error);
								SendaCustomSwal.fire({
									iconHtml: '<i class="error-icon"></i>',
									title: "Erro",
									text: "Erro ao excluir o plano de ação. Tente novamente.",
									customClass: {
										confirmButton: "custom-confirm-button",
									},
								});
							} finally {
								setLoading(false);
							}
						}
					};

					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<Tooltip title="Excluir" placement="top">
								<IconButton
									onClick={handleDelete}
									disabled={isDisabled}
									style={{
										padding: "1px",
										cursor: isDisabled ? "not-allowed" : "pointer",
										opacity: isDisabled ? 0.5 : 1,
									}}
								>
									<Trash size={14} color={"black"} />
								</IconButton>
							</Tooltip>
						</div>
					);
				},
			},
			{
				header: "Quem pode ver",
				accessorKey: "collaborators",
				widthPixels: planosDeAcaoZoom.getColumnWidth('collaborators'),
				minWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS.collaborators.min,
				maxWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS.collaborators.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				filterFn: "contains",
				accessorFn: (row) => {
					const collaborators = row.collaborators || [];
					return collaborators.map((c) => c.lastname).join(", ");
				},
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					const collaborators = row.original.collaborators || [];
					const collaboratorNames = collaborators
						.map((c) => c.lastname)
						.join(", ");

					return (
						<span
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{collaboratorNames}
						</span>
					);
				},
			},
			{
				header: <span>Marcar</span>,
				accessorKey: "id",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: planosDeAcaoZoom.getColumnWidth('id'),
				minWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS.id.min,
				maxWidthPixels: PLANOS_DE_ACAO_COLUMN_WIDTHS.id.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<div
								style={{
									marginLeft: 12,
									cursor: isDisabled
										? "not-allowed"
										: "pointer", // Aplicando o cursor aqui
									opacity: isDisabled ? 0.5 : 1, // Diminui a opacidade se estiver desabilitado
								}}
							>
								<Checkbox
									className="form-check-input"
									key={`checkbox-${row.original.id}`}
									id={`checkbox-${row.original.id}`}
									name={`checkbox-${row.original.id}`}
									checked={!!selectedRows[row.original.id]}
									onChange={(event) => {
										if (isDisabled) return; // Não faz nada se a linha estiver desabilitada
										const value = event.target.checked;
										setSelectedRows((prev) => ({
											...prev,
											[row.original.id]: value,
										}));
									}}
									style={{
										background: "none",
										border: "none",
										padding: 0,
										color: "black",
									}}
									icon={<Square size={16} />}
									checkedIcon={<CheckSquare size={16} />}
									disabled={isDisabled} // Desativa o checkbox se a linha for desabilitada
								/>
							</div>
						</div>
					);
				},
			},
		],
		[selectedRows, planosDeAcaoZoom.zoomLevel], // Adicionado zoomLevel como dependência
	);

	const inteligenciaEmpresarialColumn = useMemo(
		() => [
			{
				header: "Power BI",
				accessorKey: "name",
				widthPixels: inteligenciaEmpresarialZoom.getColumnWidth('name'),
				minWidthPixels: INTELIGENCIA_EMPRESARIAL_COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				filterFn: "contains",
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					return (
						<div
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{row.getValue("name")}
						</div>
					);
				},
			},
			{
				header: "Quem pode ver",
				accessorKey: "users",
				widthPixels: inteligenciaEmpresarialZoom.getColumnWidth('users'),
				minWidthPixels: INTELIGENCIA_EMPRESARIAL_COLUMN_WIDTHS.users.min,
				maxWidthPixels: INTELIGENCIA_EMPRESARIAL_COLUMN_WIDTHS.users.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				filterFn: "contains",
				accessorFn: (row) => {
					const users = row.users || [];
					return users
						.map((u) => u.user?.lastname)
						.filter(Boolean)
						.join(", ");
				},
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);
					const collaborators = row.original.users || [];
					const collaboratorNames = collaborators
						.map((c) => c.user?.lastname)
						.filter(Boolean)
						.join(", ");

					return (
						<span
							style={{
								opacity: isDisabled ? 0.5 : 1,
							}}
						>
							{collaboratorNames}
						</span>
					);
				},
			},
			{
				header: <span>Marcar</span>,
				accessorKey: "id",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: inteligenciaEmpresarialZoom.getColumnWidth('id'),
				minWidthPixels: INTELIGENCIA_EMPRESARIAL_COLUMN_WIDTHS.id.min,
				maxWidthPixels: INTELIGENCIA_EMPRESARIAL_COLUMN_WIDTHS.id.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => {
					const isDisabled = disabledRows.includes(row.original.id);

					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<div
								style={{
									marginLeft: 12,
									cursor: isDisabled
										? "not-allowed"
										: "pointer", // Aplicando o cursor aqui
									opacity: isDisabled ? 0.5 : 1, // Diminui a opacidade se estiver desabilitado
								}}
							>
								<Checkbox
									className="form-check-input"
									key={`checkbox-${row.original.id}`}
									id={`checkbox-${row.original.id}`}
									name={`checkbox-${row.original.id}`}
									checked={!!selectedRows[row.original.id]}
									onChange={(event) => {
										if (isDisabled) return; // Não faz nada se a linha estiver desabilitada
										const value = event.target.checked;
										setSelectedRows((prev) => ({
											...prev,
											[row.original.id]: value,
										}));
									}}
									style={{
										background: "none",
										border: "none",
										padding: 0,
										color: "black",
									}}
									icon={<Square size={16} />}
									checkedIcon={<CheckSquare size={16} />}
									disabled={isDisabled} // Desativa o checkbox se a linha for desabilitada
								/>
							</div>
						</div>
					);
				},
			},
		],
		[selectedRows, inteligenciaEmpresarialZoom.zoomLevel], // Adicionado zoomLevel como dependência
	);

	return (
		<div className="page-content">
			<Breadcrumbs title="Permissões de visualização" />
			<Loading isLoading={loading}>
				<div className="container-fluid">
					<FormGroup>
						<Row>
							{/* Seleção do departamento */}
							<Col md={4}>
								<Select
									styles={customStyles}
									placeholder="Selecione o departamento"
									onChange={(selectedOption) => {
										const selectedValue = selectedOption
											? selectedOption.value
											: "";
										validation.setFieldValue(
											"department",
											selectedValue,
										);
									}}
									onBlur={() =>
										validation.setFieldTouched(
											"department",
											true,
										)
									}
									value={
										departments.find(
											(option) =>
												option.value ===
												validation.values.department,
										) || null
									}
									options={departments}
									isInvalid={
										validation.touched.department &&
										Boolean(validation.errors.department)
									}
									menuPortalTarget={document.body}
								/>
								{validation.touched.department &&
									validation.errors.department ? (
									<div style={{ color: "red", marginTop: 5 }}>
										{validation.errors.department}
									</div>
								) : null}
							</Col>

							{/* Seleção de quem pode ver */}
							<Col md={4}>
								<Select
									styles={customStyles}
									placeholder="Selecione quem pode ver"
									onChange={(selectedOptions) => {
										const selectedValues = selectedOptions
											? selectedOptions.map(
												(option) => option.value,
											)
											: [];
										validation.setFieldValue(
											"whoCanView",
											selectedValues,
										);
									}}
									onBlur={() =>
										validation.setFieldTouched(
											"whoCanView",
											true,
										)
									}
									value={
										whoCanView.filter((option) =>
											validation.values.whoCanView.includes(
												option.value,
											),
										) || []
									}
									options={whoCanView}
									isMulti
									isInvalid={
										validation.touched.whoCanView &&
										Boolean(validation.errors.whoCanView)
									}
									menuPortalTarget={document.body}
									menuPosition="fixed"
								/>
								{validation.touched.whoCanView &&
									validation.errors.whoCanView ? (
									<FormFeedback type="invalid">
										{validation.errors.whoCanView}
									</FormFeedback>
								) : null}
							</Col>

							{/* Botões */}
							<Col
								md={4}
								style={{
									display: "flex",
									justifyContent: "space-between",
								}}
							>
								<Tooltip
									title={
										isAnyRowSelected()
											? ""
											: "Selecione ao menos uma ata e um colaborador para ativar o botão."
									}
									placement="top"
									disableInteractive
								>
									<span>
										<Button
											style={{
												background: "none",
												color: "black",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 2,
												height: "38px",
												whiteSpace: "nowrap",
												opacity: isAnyRowSelected()
													? 1
													: 0.5,
											}}
											onClick={validation.handleSubmit}
											disabled={!isAnyRowSelected()}
										>
											<Plus size={16} />
											Inserir
										</Button>
									</span>
								</Tooltip>
								<Tooltip
									title={
										isAnyRowSelected()
											? ""
											: "Selecione ao menos uma ata e um colaborador para ativar o botão."
									}
									placement="top"
									disableInteractive
								>
									<span>
										<Button
											style={{
												background: "none",
												color: "black",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 2,
												height: "38px",
												whiteSpace: "nowrap",
												opacity: isAnyRowSelected()
													? 1
													: 0.5,
											}}
											onClick={removeCollaborators}
											disabled={!isAnyRowSelected()}
										>
											<X size={16} />
											Remover
										</Button>
									</span>
								</Tooltip>
								{Object.keys(selectedRows)
									.filter(
										(key) => !disabledRows.includes(key),
									)
									.every((key) => selectedRows[key]) ? (
									// Botão "Desmarcar todos"
									<Button
										style={{
											background: "none",
											color: "black",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 2,
											height: "38px",
											whiteSpace: "nowrap",
										}}
										onClick={() => {
											const noneSelected =
												Object.fromEntries(
													Object.keys(
														selectedRows,
													).map((key) => [
														key,
														false,
													]),
												);
											setSelectedRows(noneSelected);
										}}
									>
										<Square size={16} />
										Desmarcar todos
									</Button>
								) : (
									// Botão "Marcar todos"
									<Button
										style={{
											background: "none",
											color: "black",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 2,
											height: "38px",
											whiteSpace: "nowrap",
										}}
										onClick={() => {
											const allSelected =
												Object.fromEntries(
													Object.keys(
														selectedRows,
													).map((key) => [
														key,
														!disabledRows.includes(
															key,
														),
													]),
												);
											setSelectedRows(allSelected);
										}}
									>
										<CheckSquare size={16} />
										Marcar todos
									</Button>
								)}
							</Col>
						</Row>
					</FormGroup>

					{validation.values.department === 1 && (
						<DynamicMaterialTable
							columns={atasColumn}
							data={atasData}
						/>
					)}

					{validation.values.department === 2 && (
						<DynamicMaterialTable
							columns={indicadoresColumn}
							data={indicadoresData}
						/>
					)}

					{validation.values.department === 3 && (
						<DynamicMaterialTable
							columns={planosDeAcaoColumn}
							data={planosDeAcaoData}
						/>
					)}

					{validation.values.department === 4 && (
						<DynamicMaterialTable
							columns={inteligenciaEmpresarialColumn}
							data={inteligenciaEmpresarialData}
						/>
					)}
				</div>
			</Loading>
		</div>
	);
};
ViewingPermissions.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(ViewingPermissions);
